import React from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import cloneDeep from "lodash/cloneDeep";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Grid from "@cloudscape-design/components/grid";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Tabs from "@cloudscape-design/components/tabs";
import Alert from "@cloudscape-design/components/alert";
import Spinner from "@cloudscape-design/components/spinner";
import Select from "@cloudscape-design/components/select";
import Multiselect from "@cloudscape-design/components/multiselect";
import Container from "@cloudscape-design/components/container";
import DatePicker from "@cloudscape-design/components/date-picker";
import Modal from "@cloudscape-design/components/modal";

import ProgressiveImage from "react-progressive-graceful-image";
import Poster from "../assets/poster.png";
import { formatChestPrizeType } from "../helpers/chestPrizeTypes";

const parseChestRewardConditions = (value, fallback = {}) => {
	if (Array.isArray(value)) {
		return value;
	}

	if (value && typeof value === "object") {
		return value;
	}

	if (typeof value === "string" && value.trim().length) {
		try {
			return JSON.parse(value);
		} catch (error) {
			return fallback;
		}
	}

	return fallback;
};

const getChestRewardsPayload = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions, []);

	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (Array.isArray(parsed?.prizes)) {
		return parsed.prizes;
	}

	return [];
};

const getChestTypeFromRewardConditions = (rewardConditions) => {
	const parsed = parseChestRewardConditions(rewardConditions);

	if (Array.isArray(parsed)) {
		return "";
	}

	return parsed?.type || "";
};

const normalizeChestPrizeValue = (value) => {
	if (Array.isArray(value)) {
		return value.map((entry) => normalizeChestPrizeValue(entry));
	}

	if (value && typeof value === "object") {
		return Object.keys(value)
			.sort()
			.reduce((accumulator, key) => {
				accumulator[key] = normalizeChestPrizeValue(value[key]);
				return accumulator;
			}, {});
	}

	return value;
};

const getChestPrizeSignature = ({ type, rewardConditions }) =>
	JSON.stringify({
		type: type || "",
		rewardConditions: normalizeChestPrizeValue(getChestRewardsPayload(rewardConditions)),
	});

const findMatchingChestPrize = (items = [], { type, rewardConditions }) => {
	const targetSignature = getChestPrizeSignature({ type, rewardConditions });

	return (
		(items || []).find(
			(item) =>
				getChestPrizeSignature({
					type: item.type,
					rewardConditions: item.rewardConditions,
				}) === targetSignature,
		) || null
	);
};

const formatChestRewardsSummary = (rewardConditions, fallback = "Chest config") => {
	const rewards = getChestRewardsPayload(rewardConditions);
	if (!rewards.length) {
		return fallback;
	}

	const totalProbability = rewards.reduce((sum, reward) => sum + (Number(reward.probability) || 0), 0);

	return `Chest • ${rewards.length} rewards • ${totalProbability}%`;
};

const getChestPrizeDisplayLabel = (items = [], { type, rewardConditions, fallback = "Chest config" }) => {
	const match = findMatchingChestPrize(items, { type, rewardConditions });

	return match ? `${match.name} (${match.id})` : formatChestRewardsSummary(rewardConditions, fallback);
};

const parseJsonArray = (value) => {
	if (Array.isArray(value)) return value;
	if (!value) return [];

	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value || "[]");
			return Array.isArray(parsed) ? parsed : [];
		} catch (error) {
			return [];
		}
	}

	return [];
};

const ITEM_WINNER_STATUS_OPTIONS = [
	{ value: "processing", label: "Processing", color: "#5f6b7a" },
	{ value: "processed", label: "Processed", color: "#0972d3" },
	{ value: "waiting", label: "Waiting", color: "#b08800" },
	{ value: "rejected", label: "Rejected", color: "#d91515" },
	{ value: "approved", label: "Approved", color: "#037f0c" },
];

const getItemWinnerStatusOption = (status) => ITEM_WINNER_STATUS_OPTIONS.find((option) => option.value === status) || ITEM_WINNER_STATUS_OPTIONS[0];

const ITEM_WINNER_STATUS_ACTIONS = {
	processing: [{ status: "processed", label: "Mark as processed", variant: "primary" }],
	processed: [
		{ status: "rejected", label: "Reject" },
		{ status: "approved", label: "Approve", variant: "primary" },
		{ status: "waiting", label: "Hold" },
	],
	waiting: [
		{ status: "rejected", label: "Reject" },
		{ status: "approved", label: "Approve", variant: "primary" },
	],
	rejected: [],
	approved: [],
};

const PRIZE_REWARD_TYPE_FILTER_OPTIONS = [
	{ value: "", label: "All reward types" },
	{ value: "money", label: "Money" },
	{ value: "freespins", label: "Freespins" },
	{ value: "tickets", label: "Tickets" },
	{ value: "item", label: "Item" },
	{ value: "chest", label: "Chest" },
];

const PRIZE_ACTIVE_FILTER_OPTIONS = [
	{ value: "", label: "All statuses" },
	{ value: "1", label: "Active" },
	{ value: "0", label: "Inactive" },
];

const PRIZE_ACTIVE_TAB_SESSION_KEY = "prize.activeTab";
const PRIZE_TAB_IDS = ["list", "stats", "itemWinners", "tickets"];

const getStoredPrizeActiveTab = () => {
	try {
		const storedTab = sessionStorage.getItem(PRIZE_ACTIVE_TAB_SESSION_KEY);
		return PRIZE_TAB_IDS.includes(storedTab) ? storedTab : "list";
	} catch (error) {
		return "list";
	}
};

const setStoredPrizeActiveTab = (activeTab) => {
	try {
		if (PRIZE_TAB_IDS.includes(activeTab)) {
			sessionStorage.setItem(PRIZE_ACTIVE_TAB_SESSION_KEY, activeTab);
		}
	} catch (error) {}
};

/* Recoil */
import { prizeStore } from "../stores/prize";
import { configStore } from "../stores/config";
import { prizeAddModalStore } from "../stores/prizeAddModal";
import { prizeEditModalStore } from "../stores/prizeEditModal";
import { prizeSettingsModalStore } from "../stores/prizeSettingsModal";
import { userStore } from "../stores/access";

import PrizeTickets from "./PrizeTickets";

/* ------- */

class Prize extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			activeTab: getStoredPrizeActiveTab(),
			chestPrizeItems: [],
			stats: {
				total: [],
				loaded: true,
				error: null,
				pool: [],
				chests: [],
			},
			statsFilters: {
				period: "day",
				startDate: null,
				endDate: null,
			},
			itemWinners: {
				data: [],
				loaded: true,
				error: null,
				updatingId: null,
				filters: {
					statuses: [],
					uid: "",
					prizeId: "",
					prizeInstanceId: "",
				},
				sorting: {
					column: "completedAt",
					order: "DESC",
				},
				pagination: {
					page: 1,
					limit: 20,
					total: 0,
					pages: 0,
				},
			},
			itemWinnerActionModal: {
				visible: false,
				item: null,
				pendingStatus: null,
				error: null,
			},
		};
	}

	loadChestPrizeOptions = async () => {
		try {
			const response = await this.props.Interface.getChestPrizeOptions({ page: 1, limit: 999 });
			this.setState({ chestPrizeItems: response?.items || [] });
		} catch (error) {
			console.error(error);
		}
	};

	formatPeriod = (period) => {
		if (!period) return "-";

		if (period.includes(" - ")) {
			const [start, end] = period.split(" - ");
			const formatDate = (d) => {
				const [y, m, day] = d.split(".");
				return `${day}.${m}.${y}`;
			};
			return `${formatDate(start)} - ${formatDate(end)}`;
		}

		const parts = period.split(".");
		if (parts.length === 3) {
			const [y, m, d] = parts;
			return `${d}.${m}.${y}`;
		}
		if (parts.length === 2) {
			const [y, m] = parts;
			return `${m}.${y}`;
		}

		return period;
	};

	formatStatisticMoney = (value) =>
		`${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(Number(value) / 100)} RUB.`;

	formatChestReward = (rewardType, rewardValue, rewardConditions = {}) => {
		if (rewardType === "money") return this.formatStatisticMoney(rewardValue);
		if (rewardType === "tickets") return this.props.Interface.fmtTickets(rewardValue || 0);
		if (rewardType === "freespins") {
			const betAmount = Number(rewardConditions?.betAmount) || 0;
			const freespins = this.props.Interface.fmtFs(rewardValue || 0);
			return betAmount > 0
				? `${freespins} (${this.props.Interface.fmtMoney((rewardValue || 0) * betAmount, true)})`
				: freespins;
		}

		return rewardValue || 0;
	};

	formatPercent = (value) => {
		if (value === null || typeof value === "undefined" || value === "") return "-";

		return `${Number(value) || 0}%`;
	};

	formatNumber = (value) => {
		const number = Number(value);
		if (!Number.isFinite(number)) return "-";

		return number.toLocaleString();
	};

	formatSignedPercent = (value) => {
		if (value === null || typeof value === "undefined" || value === "") return "-";

		const number = Number(value) || 0;
		return `${number > 0 ? "+" : ""}${number}%`;
	};

	formatChestLabel = (item) => {
		const type = item.chest_type ? formatChestPrizeType(item.chest_type) : "Chest";
		const name = item.chest_name && item.chest_name !== type ? ` ${item.chest_name}` : "";
		const id = item.chest_id ? ` (#${item.chest_id})` : "";

		return `${type}${name}${id}`;
	};

	formatChestPrizeLabel = (item) => {
		const type = item.nested_type || "unknown";
		const reward = this.formatChestReward(item.nested_type, item.reward_value, item.reward_conditions);

		return `${type} ${reward}`;
	};

	renderPoolTable = () => {
		const { stats } = this.state;

		if (!stats.pool || stats.pool.length === 0) {
			return (
				<Box textAlign="center" padding="l" color="text-label">
					No prize pool data for selected period
				</Box>
			);
		}

		return (
			<Table
				header={<Header variant="h2">Prize pool count</Header>}
				columnDefinitions={[
					{ id: "id", header: "ID", cell: (item) => item.prize_id },
					{ id: "name", header: "Name", width: 800, maxWidth: 800, cell: (item) => item.prize_name },
					{
						id: "type",
						header: "Type",
						cell: (item) => (item.source_reward_type === "chest" ? `chest (${item.chest_reward_type || "unknown"})` : item.prize_type),
					},
					{
						id: "reward",
						header: "Reward",
						cell: (item) =>
							item.source_reward_type === "chest"
								? this.formatChestReward(item.chest_reward_type, item.chest_reward_value, item.chest_reward_conditions)
								: item.prize_type === "money"
									? this.formatStatisticMoney(item.prize_reward)
									: item.prize_reward
						},
						{ id: "remaining", header: "Remaining", cell: item => item.remaining_count },
						{ id: "start_time", header: "Start time", cell: item => item.start_time ? new Date(item.start_time).toLocaleDateString() : "Not used" },
						{ id: "end_time", header: "End time", cell: item => item.end_time ? new Date(item.end_time).toLocaleDateString() : "Not used" },
					]}
					items={stats.pool}
				/>
		);
	};
	componentDidMount() {
		this.props.Interface.loadPrizeList();
		this.loadChestPrizeOptions();
		if (this.state.activeTab === "itemWinners") {
			this.loadItemWinners(1);
		}
	}

	getChestDisplayLabel = (item) =>
		getChestPrizeDisplayLabel(this.state.chestPrizeItems, {
			type: this.getChestType(item),
			rewardConditions: item.rewardConditions,
			fallback: "Chest config",
		});

	getChestType = (item) => item.type || getChestTypeFromRewardConditions(item.rewardConditions);

	loadStats = async () => {
		this.setState({
			stats: {
				general: [],
				pool: [],
				chests: [],
				loaded: false,
				error: null,
			},
		});

		try {
			const { period, startDate, endDate } = this.state.statsFilters;

			if (!startDate || !endDate) {
				this.setState({
					stats: {
						general: [],
						pool: [],
						chests: [],
						loaded: true,
						error: "Please select start and end date",
					},
				});
				return;
			}

			const result = await this.props.Interface.prizeGetStatistic({
				period,
				start_date: startDate,
				end_date: endDate,
			});

			this.setState({
				stats: {
					general: result.general || [],
					pool: result.pool || [],
					chests: result.chests || [],
					loaded: true,
					error: null,
				},
			});
		} catch (err) {
			console.error("Prize stats load error:", err);
			this.setState({
				stats: {
					general: [],
					pool: [],
					chests: [],
					loaded: true,
					error: err.message || "Failed to load statistics",
				},
			});
		}
	};

	loadItemWinners = async (page = this.state.itemWinners.pagination.page) => {
		const { filters, sorting, pagination } = this.state.itemWinners;
		this.setState((prev) => ({
			itemWinners: {
				...prev.itemWinners,
				loaded: false,
				error: null,
				pagination: {
					...prev.itemWinners.pagination,
					page,
				},
			},
		}));

		try {
			const result = await this.props.Interface.prizeItemWinners({
				page,
				limit: pagination.limit,
				filters,
				sorting,
			});

			this.setState({
				itemWinners: {
					...this.state.itemWinners,
					data: result.items || [],
					loaded: true,
					error: null,
					pagination: result.pagination || {
						page,
						limit: pagination.limit,
						total: 0,
						pages: 0,
					},
				},
			});
		} catch (error) {
			this.setState((prev) => ({
				itemWinners: {
					...prev.itemWinners,
					data: [],
					loaded: true,
					error: error?.text || error?.message || "Failed to load item winners",
				},
			}));
		}
	};

	updateItemWinnerFilter = (key, value) => {
		this.setState((prev) => ({
			itemWinners: {
				...prev.itemWinners,
				filters: {
					...prev.itemWinners.filters,
					[key]: value,
				},
			},
		}));
	};

	clearItemWinnerFilters = () => {
		this.setState(
			(prev) => ({
				itemWinners: {
					...prev.itemWinners,
					filters: {
						statuses: [],
						uid: "",
						prizeId: "",
						prizeInstanceId: "",
					},
				},
			}),
			() => this.loadItemWinners(1),
		);
	};

	handleItemWinnersSortingChange = ({ detail }) => {
		const sortingField = detail.sortingColumn?.sortingField || "completedAt";
		const isDescending = detail.isDescending !== false;

		this.setState(
			(prev) => ({
				itemWinners: {
					...prev.itemWinners,
					sorting: {
						column: sortingField,
						order: isDescending ? "DESC" : "ASC",
					},
				},
			}),
			() => this.loadItemWinners(1),
		);
	};

	canManageItemWinnerActions = () => {
		const { user } = this.props;
		const showColumn = Array.isArray(user?.showColumn) ? user.showColumn : [];

		return Number(user?.level) >= 6 || showColumn.includes("updateItemWinnerStatus");
	};

	openItemWinnerActionModal = (item) => {
		this.setState({
			itemWinnerActionModal: {
				visible: true,
				item: { ...item },
				pendingStatus: null,
				error: null,
			},
		});
	};

	closeItemWinnerActionModal = () => {
		if (this.state.itemWinners.updatingId !== null) {
			return;
		}

		this.setState({
			itemWinnerActionModal: {
				visible: false,
				item: null,
				pendingStatus: null,
				error: null,
			},
		});
	};

	updateItemWinnerStatus = async (item, status) => {
		const currentPage = this.state.itemWinners.pagination.page;
		this.setState((prev) => ({
			itemWinners: {
				...prev.itemWinners,
				updatingId: item.id,
				error: null,
			},
			itemWinnerActionModal: {
				...prev.itemWinnerActionModal,
				pendingStatus: status,
				error: null,
			},
		}));

		try {
			await this.props.Interface.prizeUpdateItemWinnerStatus({
				id: item.id,
				roundId: item.roundId,
				status,
			});

			this.setState(
				(prev) => ({
					itemWinners: {
						...prev.itemWinners,
						updatingId: null,
					},
					itemWinnerActionModal: {
						visible: false,
						item: null,
						pendingStatus: null,
						error: null,
					},
				}),
				() => this.loadItemWinners(currentPage),
			);
		} catch (error) {
			this.setState((prev) => ({
				itemWinners: {
					...prev.itemWinners,
					updatingId: null,
					error: error?.text || error?.message || "Failed to update item winner status",
				},
				itemWinnerActionModal: {
					...prev.itemWinnerActionModal,
					pendingStatus: null,
					error: error?.text || error?.message || "Failed to update item winner status",
				},
			}));
		}
	};

	renderItemWinnerActionModal = () => {
		const { itemWinnerActionModal, itemWinners } = this.state;
		const item = itemWinnerActionModal.item;
		const actions = item ? ITEM_WINNER_STATUS_ACTIONS[item.status] || [] : [];
		const statusOption = getItemWinnerStatusOption(item?.status);
		const isUpdating = item && itemWinners.updatingId === item.id;

		return (
			<Modal
				visible={itemWinnerActionModal.visible}
				onDismiss={this.closeItemWinnerActionModal}
				header={item ? `Item Winner #${item.id}` : "Item Winner Actions"}
				footer={
					<Box float="right">
						<Button variant="link" disabled={isUpdating} onClick={this.closeItemWinnerActionModal}>
							Close
						</Button>
					</Box>
				}>
				{item && (
					<SpaceBetween direction="vertical" size="m">
						<Box>
							<b>UID:</b> {item.uid} | <b>Prize:</b> {item.prizeName || "-"} | <b>Round ID:</b> {item.roundId}
						</Box>
						<Box>
							<b>Status:</b>{" "}
							<span style={{ backgroundColor: statusOption.color, padding: "2px 8px", borderRadius: "5px", color: "#fff", fontWeight: 600 }}>{statusOption.label}</span>
						</Box>
						{itemWinnerActionModal.error && <Alert type="error">{itemWinnerActionModal.error}</Alert>}
						{actions.length ? (
							<SpaceBetween direction="horizontal" size="xs">
								{actions.map((action) => (
									<Button
										key={action.status}
										variant={action.variant || "normal"}
										loading={isUpdating && itemWinnerActionModal.pendingStatus === action.status}
										disabled={isUpdating}
										onClick={() => this.updateItemWinnerStatus(item, action.status)}>
										{action.label}
									</Button>
								))}
							</SpaceBetween>
						) : (
							<Alert type="info">No actions are available for this status.</Alert>
						)}
					</SpaceBetween>
				)}
			</Modal>
		);
	};

	handlePrizeListSortingChange = ({ detail }) => {
		const sortingField = detail.sortingColumn?.sortingField || "id";
		const isDescending = detail.isDescending === true;
		const { prize } = this.props;

		this.props.setPrize({
			...prize,
			sorting: {
				sortingColumn: {
					order: isDescending ? "DESC" : "ASC",
					column: sortingField,
				},
				isDescending,
			},
			pagination: {
				...prize.pagination,
				current: 1,
			},
		});

		setTimeout(() => this.props.Interface.loadPrizeList(), 0);
	};

	updatePrizeListFilter = (key, value) => {
		const { prize } = this.props;

		this.props.setPrize({
			...prize,
			filters: {
				...prize.filters,
				[key]: value,
			},
		});
	};

	applyPrizeListFilters = () => {
		const { prize } = this.props;

		this.props.setPrize({
			...prize,
			pagination: {
				...prize.pagination,
				current: 1,
			},
		});

		setTimeout(() => this.props.Interface.loadPrizeList(), 0);
	};

	clearPrizeListFilters = () => {
		const { prize } = this.props;

		this.props.setPrize({
			...prize,
			filters: {
				name: "",
				rewardType: "",
				active: "",
			},
			pagination: {
				...prize.pagination,
				current: 1,
			},
		});

		setTimeout(() => this.props.Interface.loadPrizeList(), 0);
	};

	renderPrizeListTab = () => {
		const { prize } = this.props;
		const filters = prize.filters || {};
		const selectedRewardType = PRIZE_REWARD_TYPE_FILTER_OPTIONS.find((option) => option.value === filters.rewardType) || PRIZE_REWARD_TYPE_FILTER_OPTIONS[0];
		const selectedActive = PRIZE_ACTIVE_FILTER_OPTIONS.find((option) => option.value === filters.active) || PRIZE_ACTIVE_FILTER_OPTIONS[0];

		return (
			<SpaceBetween direction="vertical" size="l">
				<Container header={<Header variant="h2">Prize List Filters</Header>}>
					<SpaceBetween size="m">
						<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
							<Input value={filters.name || ""} onChange={({ detail }) => this.updatePrizeListFilter("name", detail.value)} placeholder="Name" />
							<Select selectedOption={selectedRewardType} options={PRIZE_REWARD_TYPE_FILTER_OPTIONS} onChange={({ detail }) => this.updatePrizeListFilter("rewardType", detail.selectedOption.value)} />
							<Select selectedOption={selectedActive} options={PRIZE_ACTIVE_FILTER_OPTIONS} onChange={({ detail }) => this.updatePrizeListFilter("active", detail.selectedOption.value)} />
						</Grid>
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="primary" onClick={this.applyPrizeListFilters}>
								Apply
							</Button>
							<Button onClick={this.clearPrizeListFilters}>Clear</Button>
						</SpaceBetween>
					</SpaceBetween>
				</Container>

				<Table
				stickyHeader
				trackBy="id"
				loading={!prize.loaded}
				sortingColumn={{ sortingField: prize.sorting.sortingColumn.column }}
				sortingDescending={prize.sorting.isDescending}
				onSortingChange={this.handlePrizeListSortingChange}
				columnDefinitions={[
					{
						id: "id",
						header: "ID",
						sortingField: "id",
						cell: (item) => (
							<Button
								variant="normal"
								onClick={() => {
									const prizeToEdit = { ...item };
									delete prizeToEdit.winnerCohortConditions;

									this.props.setPrizeEditModalStore({
										...this.props.prizeEditModal,
										show: true,
										...prizeToEdit,
										active: item.active ? { value: "1", label: "Enabled" } : { value: "0", label: "Disabled" },
										rewardValue: item.rewardType === "chest" ? "" : item.rewardType === "money" ? item.rewardValue / 100 : item.rewardValue,
										fakeWinsPerMonth: item.fakeWinsPerMonth ?? "",
										conditions: parseJsonArray(item.cohortConditions),
										poolType: this.props.prizeEditModal.currentOption.poolType.find((element) => element.value === item.poolType),
										rewardType: this.props.prizeEditModal.currentOption.type.find((element) => element.value === item.rewardType),
									});
								}}
								children={item.id}
							/>
						),
					},
					{
						id: "image",
						header: "Image",
						width: 80,
						cell: (item) => (
							<ProgressiveImage src={item.image || Poster} placeholder={Poster}>
								{(src) => <img style={{ width: "70px" }} src={src} alt={item.name || "prize"} />}
							</ProgressiveImage>
						),
					},
					{
						id: "icon",
						header: "Icon",
						width: 80,
						cell: (item) =>
							item.icon ? (
								<ProgressiveImage src={item.icon || Poster} placeholder={Poster}>
									{(src) => <img style={{ width: "70px" }} src={src} alt={item.name || "prize"} />}
								</ProgressiveImage>
							) : (
								"N/A"
							),
					},
					{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name },
					{ id: "description", header: "Description", cell: (item) => item.description },
					{
						id: "rewardType",
						header: "Reward Type",
						sortingField: "rewardType",
						cell: (item) => (item.rewardType === "chest" ? `Chest${this.getChestType(item) ? ` (${formatChestPrizeType(this.getChestType(item))})` : ""}` : item.rewardType),
					},
					{
						id: "rewardValue",
						header: "Value",
						cell: (item) => (item.rewardType === "chest" ? this.getChestDisplayLabel(item) : item.rewardType === "money" ? item.rewardValue / 100 : item.rewardValue),
					},
					{ id: "probability", header: "Probability", sortingField: "probability", cell: (item) => item.probability },
					{ id: "poolCount", header: "Pool Count", cell: (item) => item.poolCount },
					{ id: "order", header: "Order", sortingField: "order", cell: (item) => item.order },
					{ id: "poolType", header: "Pool Type", sortingField: "poolType", cell: (item) => item.poolType },
					{ id: "active", header: "Active", sortingField: "active", cell: (item) => (item.active ? "Yes" : "No") },
				]}
				items={prize.data}
				pagination={
					<Pagination
						currentPageIndex={prize.pagination.current}
						pagesCount={prize.pagination.total}
						onChange={({ detail }) => {
							this.props.setPrize({ ...prize, pagination: { ...prize.pagination, current: detail.currentPageIndex } });
							setTimeout(() => this.props.Interface.loadPrizeList(), 100);
						}}
					/>
				}
				header={
					<Header
						actions={
							<SpaceBetween direction="horizontal" size="xs">
								<Button onClick={() => this.props.setPrizeSettingsModalStore({ ...this.props.prizeSettingsModal, show: true })}>Prize Settings</Button>
								<Button variant="primary" onClick={() => this.props.setPrizeAddModalStore({ ...this.props.prizeAddModal, show: true })}>
									Add Prize
								</Button>
							</SpaceBetween>
						}
						counter={`(${prize.pagination.count})`}>
						Prize List
					</Header>
				}
				empty="No prizes found"
				/>
			</SpaceBetween>
		);
	};

	renderStatsFilters = () => {
		const { statsFilters } = this.state;

		return (
			<Container header={<Header variant="h2">Prize Statistics</Header>}>
				<Box margin={{ bottom: "xl" }}>
					<Grid gridDefinition={[{ colspan: 12 }]}>
						<SpaceBetween direction="horizontal" size="xs">
							<Select
								selectedOption={{
									value: statsFilters.period,
									label: statsFilters.period === "day" ? "Day" : statsFilters.period === "week" ? "Week" : "Month",
								}}
								onChange={({ detail }) => this.setState({ ...this.state, statsFilters: { ...this.state.statsFilters, period: detail.selectedOption.value } })}
								options={[
									{ value: "day", label: "Day" },
									{ value: "week", label: "Week" },
									{ value: "month", label: "Month" },
								]}
							/>

							<DatePicker value={statsFilters.startDate} onChange={({ detail }) => this.setState({ ...this.state, statsFilters: { ...this.state.statsFilters, startDate: detail.value } })} placeholder="Start date" />

							<DatePicker value={statsFilters.endDate} onChange={({ detail }) => this.setState({ ...this.state, statsFilters: { ...this.state.statsFilters, endDate: detail.value } })} placeholder="End date" />

							<Button variant="primary" onClick={this.loadStats}>
								Submit
							</Button>
						</SpaceBetween>
					</Grid>
				</Box>
			</Container>
		);
	};

	renderGeneralStatsTable = () => {
		const { stats } = this.state;
		const periods = [...new Set(stats.general.map((r) => r.period))].sort((a, b) => b.localeCompare(a));

		const rows = [
			{ metric: "Started Rounds", key: "started_rounds" },
			{ metric: "Completed Rounds", key: "completed_rounds" },
			{ metric: "Completion Rate %", key: "completion_rate_pct", format: (v) => (v ? `${v}%` : "0%") },
			{ metric: "Started (Unique)", key: "started_unique" },
			{ metric: "Completed (Unique)", key: "completed_unique" },
			{ metric: "Completion Rate (Unique) %", key: "completion_rate_unique_pct", format: (v) => (v ? `${v}%` : "0%") },
			{ metric: "Wins (Prize)", key: "wins_count" },
			{ metric: "Losses (Duck)", key: "losses_count" },
			{ metric: "Money Issued", key: "money", format: (v) => this.props.Interface.fmtMoney(v || 0) },
			{ metric: "Tickets Issued", key: "tickets", format: (v) => this.props.Interface.fmtTickets(v || 0) },
			{
				metric: "Freespins Issued",
				key: "freespins",
				format: (v, row) => {
					const vol = row?.freespins_total || 0;
					return vol > 0 ? `${this.props.Interface.fmtFs(v || 0)} (${this.props.Interface.fmtMoney(vol, true)})` : this.props.Interface.fmtFs(v || 0);
				},
			},
			{ metric: "Chest Money Issued", key: "chest_money", format: v => this.formatStatisticMoney(v,) },
			{ metric: "Chest Tickets Issued", key: "chest_tickets", format: v => this.props.Interface.fmtTickets(v || 0) },
			{
				metric: "Chest Freespins Issued",
				key: "chest_freespins",
				format: (v, row) => {
					const vol = row?.chest_freespins_total || 0;
					return vol > 0
						? `${this.props.Interface.fmtFs(v || 0)} (${this.props.Interface.fmtMoney(vol, true)})`
						: this.props.Interface.fmtFs(v || 0);
				}
			},
		];

		return (
			<Table
				header={<Header variant="h2">Prize Game Statistics</Header>}
				columnDefinitions={[
					{ id: "metric", header: "Metric", cell: (item) => <Box fontWeight="bold">{item.metric}</Box>, width: 300 },
					...periods.map((p) => ({
						id: p,
						header: (
							<Box textAlign="center" fontWeight="bold">
								{this.formatPeriod(p)}
							</Box>
						),
						cell: (item) => {
							const row = stats.general.find((r) => r.period === p) || {};
							const val = row[item.key];
							const formatted = item.format ? item.format(val, row) : this.props.Interface.fmt(val || 0);
							return <Box textAlign="center">{formatted}</Box>;
						},
					})),
				]}
				items={rows}
			/>
		);
	};

	renderChestStatsTable = () => {
		const { stats } = this.state;

		if (!stats.chests || stats.chests.length === 0) {
			return <Box textAlign="center" padding="l" color="text-label">No chest statistics for selected period</Box>;
		}

		return (
			<Table
				header={
					<Header
						variant="h2"
						description="Current Config Chance is compared with the current chest preset; old drops can differ if the preset was changed after users opened chests."
					>
						Chest Prize Statistics
					</Header>
				}
				columnDefinitions={[
					{ id: "chest", header: "Chest", cell: item => this.formatChestLabel(item), width: 240 },
					{ id: "total_opens", header: "Total Opens", cell: item => this.formatNumber(item.total_opens) },
					{ id: "prize", header: "Prize", cell: item => this.formatChestPrizeLabel(item), width: 180 },
					{ id: "configured_chance", header: "Current Config Chance", cell: item => this.formatPercent(item.configured_chance_pct) },
					{ id: "actual_drop", header: "Actual Chance", cell: item => this.formatPercent(item.actual_drop_pct) },
					{ id: "expected_drops", header: "Expected Drops", cell: item => this.formatNumber(item.expected_drops) },
					{ id: "drops", header: "Drops", cell: item => this.formatNumber(item.drops) },
					{ id: "delta", header: "Delta", cell: item => this.formatSignedPercent(item.delta_pct) },
					{ id: "issued_total", header: "Issued Total", cell: item => this.formatChestReward(item.nested_type, item.issued_total, item.reward_conditions) },
					{ id: "unique_players", header: "Unique Players", cell: item => this.formatNumber(item.unique_players) },
				]}
				items={stats.chests}
			/>
		);
	};

	renderStatsTab = () => {
		const { stats } = this.state;
		const hasStatsData = (stats.general && stats.general.length > 0) || (stats.pool && stats.pool.length > 0) || (stats.chests && stats.chests.length > 0);

		return (
			<>
				{this.renderStatsFilters()}

				{stats.error && (
					<Box margin={{ top: "m" }}>
						<Alert type="error" header="Error">
							{stats.error}
						</Alert>
					</Box>
				)}

				{!stats.loaded && (
					<Box textAlign="center" padding="xxl">
						<Spinner size="large" />
					</Box>
				)}

				<Box margin={{ top: "m" }}></Box>

				{stats.loaded && stats.general && stats.general.length > 0 && <Box margin={{ bottom: "xxl" }}>{this.renderGeneralStatsTable()}</Box>}

				{stats.loaded && stats.pool && stats.pool.length > 0 && <Box>{this.renderPoolTable()}</Box>}

				{stats.loaded && stats.chests && stats.chests.length > 0 && (
					<Box margin={{ top: "xxl" }}>
						{this.renderChestStatsTable()}
					</Box>
				)}

				{stats.loaded && !hasStatsData && (
					<Box textAlign="center" padding="xxl" color="text-label">
						No statistics for selected period
					</Box>
				)}
			</>
		);
	};

	renderTicketsTab = () => {
		return <PrizeTickets Interface={this.props.Interface} />;
	};

	renderItemWinnersTab = () => {
		const { itemWinners } = this.state;
		const selectedStatusOptions = ITEM_WINNER_STATUS_OPTIONS.filter((option) => itemWinners.filters.statuses.includes(option.value));
		const canManageItemWinnerActions = this.canManageItemWinnerActions();

		return (
			<SpaceBetween direction="vertical" size="l">
				{canManageItemWinnerActions && this.renderItemWinnerActionModal()}
				<Container header={<Header variant="h2">Item Winners Filters</Header>}>
					<SpaceBetween size="m">
						<Grid gridDefinition={[{ colspan: 3 }, { colspan: 3 }, { colspan: 3 }, { colspan: 3 }]}>
							<Multiselect
								selectedOptions={selectedStatusOptions}
								onChange={({ detail }) =>
									this.updateItemWinnerFilter(
										"statuses",
										detail.selectedOptions.map((option) => option.value),
									)
								}
								options={ITEM_WINNER_STATUS_OPTIONS}
								placeholder="Filter by status"
								selectedAriaLabel="Selected"
							/>
							<Input type="number" value={itemWinners.filters.uid} onChange={({ detail }) => this.updateItemWinnerFilter("uid", detail.value)} placeholder="UID" />
							<Input type="number" value={itemWinners.filters.prizeId} onChange={({ detail }) => this.updateItemWinnerFilter("prizeId", detail.value)} placeholder="Prize ID" />
							<Input type="number" value={itemWinners.filters.prizeInstanceId} onChange={({ detail }) => this.updateItemWinnerFilter("prizeInstanceId", detail.value)} placeholder="Instance ID" />
						</Grid>
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="primary" onClick={() => this.loadItemWinners(1)}>
								Apply
							</Button>
							<Button onClick={this.clearItemWinnerFilters}>Clear</Button>
						</SpaceBetween>
					</SpaceBetween>
				</Container>

				{itemWinners.error && (
					<Box margin={{ top: "m", bottom: "m" }}>
						<Alert type="error" header="Error">
							{itemWinners.error}
						</Alert>
					</Box>
				)}
				<Table
					stickyHeader
					trackBy="id"
					loading={!itemWinners.loaded}
					sortingColumn={{ sortingField: itemWinners.sorting.column }}
					sortingDescending={itemWinners.sorting.order === "DESC"}
					onSortingChange={this.handleItemWinnersSortingChange}
					columnDefinitions={[
						{ id: "id", header: "ID", sortingField: "id", cell: (item) => item.id },
						{ id: "roundId", header: "Round ID", sortingField: "roundId", cell: (item) => item.roundId },
						{ id: "uid", header: "UID", sortingField: "uid", cell: (item) => item.uid },
						{ id: "prizeId", header: "Prize ID", sortingField: "prizeId", cell: (item) => item.prizeId },
						{ id: "prizeInstanceId", header: "Instance ID", sortingField: "prizeInstanceId", cell: (item) => item.prizeInstanceId },
						{ id: "prizeName", header: "Prize Name", sortingField: "prizeName", cell: (item) => item.prizeName },
						{
							id: "status",
							header: "Status",
							sortingField: "status",
							cell: (item) => {
								const statusOption = getItemWinnerStatusOption(item.status);
								return <p style={{ backgroundColor: statusOption.color, padding: "1px 8px", borderRadius: "5px", fontSize: "11px", color: "#fff", textAlign: "center", fontWeight: 600 }}>{statusOption.label}</p>;
							},
						},
						...(canManageItemWinnerActions
							? [
									{
										id: "statusAction",
										header: "Actions",
										cell: (item) => {
											const actions = ITEM_WINNER_STATUS_ACTIONS[item.status] || [];

											return actions.length ? (
												<Button iconName="settings" onClick={() => this.openItemWinnerActionModal(item)}>
													Actions
												</Button>
											) : (
												"-"
											);
										},
										minWidth: 130,
									},
								]
							: []),
						{
							id: "completedAt",
							header: "Completed At",
							sortingField: "completedAt",
							cell: (item) => (item.completedAt ? new Date(item.completedAt).toLocaleString() : "-"),
						},
						{
							id: "updatedAt",
							header: "Updated At",
							sortingField: "updatedAt",
							cell: (item) => (item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "-"),
						},
					]}
					items={itemWinners.data}
					pagination={<Pagination currentPageIndex={itemWinners.pagination.page} pagesCount={itemWinners.pagination.pages} onChange={({ detail }) => this.loadItemWinners(detail.currentPageIndex)} />}
					header={<Header counter={`(${itemWinners.pagination.total})`}>Item Winners</Header>}
					empty="No item winners found"
				/>
			</SpaceBetween>
		);
	};

	render() {
		const { prize } = this.props;

		return (
			<>
				{prize.error.text && (
					<Alert type="error" header="Error loading prizes">
						{prize.error.text}
					</Alert>
				)}

				<Tabs
					tabs={[
						{ label: "Prizes List", id: "list", content: this.renderPrizeListTab() },
						{ label: "Statistics", id: "stats", content: this.renderStatsTab() },
						{ label: "Item Winners", id: "itemWinners", content: this.renderItemWinnersTab() },
						{ label: "Tickets", id: "tickets", content: this.renderTicketsTab() },
					]}
					activeTabId={this.state.activeTab}
					onChange={({ detail }) => {
						setStoredPrizeActiveTab(detail.activeTabId);
						this.setState({ activeTab: detail.activeTabId });
						if (detail.activeTabId === "itemWinners" && this.state.itemWinners.loaded && !this.state.itemWinners.data.length) {
							this.loadItemWinners(1);
						}
					}}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const prize = useRecoilValue(prizeStore);
		const setPrize = useSetRecoilState(prizeStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);

		const prizeAddModal = useRecoilValue(prizeAddModalStore);
		const setPrizeAddModalStore = useSetRecoilState(prizeAddModalStore);

		const prizeEditModal = useRecoilValue(prizeEditModalStore);
		const setPrizeEditModalStore = useSetRecoilState(prizeEditModalStore);
		const prizeSettingsModal = useRecoilValue(prizeSettingsModalStore);
		const setPrizeSettingsModalStore = useSetRecoilState(prizeSettingsModalStore);
		const user = useRecoilValue(userStore);

		return <Component {...props} user={user} prizeSettingsModal={prizeSettingsModal} setPrizeSettingsModalStore={setPrizeSettingsModalStore} prizeEditModal={prizeEditModal} setPrizeEditModalStore={setPrizeEditModalStore} prizeAddModal={prizeAddModal} setPrizeAddModalStore={setPrizeAddModalStore} config={config} setConfig={setConfig} prize={prize} setPrize={setPrize} />;
	};
}

export default withRecoil(Prize);
